import React from 'react';
import styled from 'styled-components';
import { useStores } from '../../../context/storesContext';
import { formatStoreTime } from '../../../utils/storeUtils';
import Box from '../../Box';
import { CText } from '@carrefour/design-system-components-react';
import useSWR from 'swr';
import { Range } from '../../../utils/range';
import { fetcherWithStoreKey } from '../../../utils/fetcher';
import { TurnoverObjective } from '../../../types/commercial';
import { EcomServiceType } from '../../../utils/ecomServiceType';
import { useKpiVisibility } from '../../../kpis/useKpiVisibility';
import { LandingDetentionTopCards } from '../LandingDetentionCards/LandingDetentionTopCards';
import { ListDetention, ListDetentionTypology } from '../../../types/operational';
import NpsVerbatims from '../NpsVerbatims';
import { LandingCommercialCard } from '../LandingCommercialCard/LandingCommercialCard';
import { todayFormatted } from '../../../utils/dateUtils';
import { DriveOrders } from '../../../types/drive';
import MrclText from '../../marcel/MrclText';

const HeaderText = styled(CText)`
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding-bottom: 16px;
`;

const DateWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-top: 8px;
  margin-bottom: -12px;
`;

const RecentAnalysisRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

export const LandingPage: React.FC = () => {
  const { selectedStore } = useStores();
  const { openingTime, closingTime } = selectedStore || {};
  const { isKpiExists } = useKpiVisibility();

  // Capitalize first letter of weekday
  const capitalizedDate = todayFormatted.charAt(0).toUpperCase() + todayFormatted.slice(1);

  /** reserved for Live kpi data */
  const swrOptions = {
    revalidateIfStale: false,
    revalidateOnFocus: false,
  };

  // live turnover (reuses SWR cache from Live.tsx)
  const { data: directTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists('Commercial', 'Turnover')
        ? [
            `/commercial/turnover?range=${Range.Direct}`,
            selectedStore?.storeKey as string,
            selectedStore.countryKey as string,
          ]
        : null,
    ([url, storeEan, countryKey]) =>
      fetcherWithStoreKey(url, storeEan as string, countryKey as string),
    swrOptions
  );

  // past turnover (reuses SWR cache from Live.tsx)
  const { data: pastTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists('Commercial', 'Turnover')
        ? [
            `/commercial/turnover?range=${Range.DirectLastYear}`,
            selectedStore?.storeKey as string,
            selectedStore.countryKey as string,
          ]
        : null,
    ([url, storeEan, countryKey]) =>
      fetcherWithStoreKey(url, storeEan as string, countryKey as string),
    swrOptions
  );

  // objective turnover (reuses SWR cache from Live.tsx - commercial objective only requires storeKey)
  const { data: directObjectiveTurnoverData } = useSWR<TurnoverObjective>(
    () =>
      selectedStore?.storeKey && isKpiExists('Commercial', 'Turnover')
        ? [
            `/commercial/objective?range=${Range.Direct}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan as string),
    swrOptions
  );
  
  // Note: Drive endpoints are uniquely scoped by store EAN (storeKey) and do not require
  // countryKey. This matches Live.tsx and preserves SWR cache deduplication across views.
   // live Drive turnover
  const { data: directDriveTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists('Commercial', 'Turnover')
        ? [
            `/drive/turnover?range=${Range.Direct}&ecomServiceType=${EcomServiceType.EcomTotal}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan as string),
    swrOptions
  );

  // past Drive turnover
  const { data: pastDriveTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists('Commercial', 'Turnover')
        ? [
            `/drive/turnover?range=${Range.DirectLastYear}&ecomServiceType=${EcomServiceType.EcomTotal}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan as string),
    swrOptions
  );

  // objective DRIVE
  const { data: currentDriveObjectiveData } = useSWR<TurnoverObjective>(
    () =>
      selectedStore?.storeKey && isKpiExists('Commercial', 'Turnover')
        ? [
            `/drive/objective?range=${Range.Direct}&ecomServiceType=${EcomServiceType.Drive}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan as string),
    swrOptions
  );

   //Detention Top3000 PGC
  const { data: detentionPgcData, error: detentionPgcError } =
    useSWR<ListDetention>(
      () =>
        selectedStore?.storeKey && isKpiExists('Detention', 'TopPGC')
          ? [
              `/operational/detention?range=${Range.PreviousDay}&storeType=${selectedStore?.storeType?.toUpperCase()}&sector=PGC&flag=TOP3000`,

              selectedStore?.storeKey as string,
            ]
          : null,
      ([url, storeEan]) =>
        fetcherWithStoreKey(url, storeEan as string),
      swrOptions
    );

  //Detention Top200 PGC
  const { data: detentionTop200PgcData, error:  detentionTop200PgcError } =
    useSWR<ListDetention>(
      () =>
        selectedStore?.storeKey && isKpiExists('Detention', 'Top200Pgc')
          ? [
              `/operational/detention?range=${Range.PreviousDay}&storeType=${selectedStore?.storeType?.toUpperCase()}&sector=PGC&flag=TOP200`,
              selectedStore?.storeKey as string,
            ]
          : null,
      ([url, storeEan]) =>
        fetcherWithStoreKey(url,  storeEan as string),
      swrOptions
    );

  //Detention Typology T1
  const { data: detentionTypologyData, error: detentionTypologyError } =
    useSWR<ListDetentionTypology>(
      () =>
        selectedStore?.storeKey
          ? [
              `/operational/detentionTypology?storeType=${selectedStore?.storeType?.toUpperCase()}&typology=T1`,

              selectedStore?.storeKey as string,
            ]
          : null,
      ([url, storeEan]) =>
        fetcherWithStoreKey(url, storeEan as string),
      swrOptions
    );

  const { data: pastEcommerceTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists('Ecommerce', 'DriveTurnover')
        ? [
            `/drive/turnover?range=${Range.DirectLastYear}&ecomServiceType=${EcomServiceType.EcomTotal}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan as string),
    swrOptions
  );

  const { data: currentDriveOrdersData } = useSWR<DriveOrders>(
    () =>
      selectedStore?.storeKey && isKpiExists('Ecommerce', 'OrderStatus')
        ? [`/drive/orders?ecomServiceType=${EcomServiceType.Drive}&range=${Range.Direct}`, selectedStore?.storeKey as string]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan as string)
  );

  const { data: currentLadOrdersData } = useSWR<DriveOrders>(
    () =>
      selectedStore?.storeKey && isKpiExists('Ecommerce', 'OrderStatus')
        ? [
            `/drive/orders?ecomServiceType=${EcomServiceType.HomeDelivery}&range=${Range.Direct}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan as string)
  );

  const { data: lastYearDriveOrdersData } = useSWR<DriveOrders>(
    () =>
      selectedStore?.storeKey && isKpiExists('Ecommerce', 'OrderStatus')
        ? [
            `/drive/orders?range=${Range.DirectLastYear}&ecomServiceType=${EcomServiceType.Drive}&range=${Range.DirectLastYear}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan as string)
  );

  const { data: lastYearLadOrdersData } = useSWR<DriveOrders>(
    () =>
      selectedStore?.storeKey && isKpiExists('Ecommerce', 'OrderStatus')
        ? [
            `/drive/orders?range=${Range.DirectLastYear}&ecomServiceType=${EcomServiceType.HomeDelivery}&range=${Range.DirectLastYear}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan as string)
  );

  return (
    <Container>
      {/* Store Header Section */}
      <Box
        data-testid="store-header"
        style={{
          minHeight: '80px',
          backgroundColor: '#EBEBEB',
          borderBottomWidth: 'var(--border-size-s, 1px)',
          borderBottomStyle: 'solid',
          borderBottomColor: '#E5E7EB',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '16px 24px',
          marginTop: '-16px',
          marginLeft: '-16px',
          marginRight: '-16px',
        }}
      >
        <HeaderText tagStyle="h1">
          <MrclText
            style={{
              fontWeight: 700,
              fontSize: 'var(--font-size-title-m, 24px)',
            }}
          >
            {selectedStore?.storeName || 'Mon Magasin'}
          </MrclText>
          {openingTime != null && closingTime != null && (
            <MrclText
              size={'s'}
              style={{
                marginTop: '8px',
                fontWeight: 400,
                textAlign: 'left',
                fontSize: 'var(--font-size-body-xs, 12px)',
                color: 'var(--Persistent-Content-Subtler, #696969)',
              }}
            >
              {`${formatStoreTime(openingTime)} - ${formatStoreTime(closingTime)}`}
            </MrclText>
          )}
        </HeaderText>
      </Box>

      {/* Date Header */}
      <DateWrapper>
        <MrclText style={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>
          Aujourd'hui
        </MrclText>
        <MrclText style={{ fontSize: '14px', fontWeight: 400, color: '#6B7280' }}>
          {capitalizedDate}
        </MrclText>
      </DateWrapper>

      {/* Main Commercial Dashboard Card */}
      <LandingCommercialCard
        directTurnoverData={directTurnoverData}
        directDriveTurnoverData={directDriveTurnoverData}
        pastTurnoverData={pastTurnoverData}
        pastDriveTurnoverData={pastDriveTurnoverData}
        directObjectiveTurnoverData={directObjectiveTurnoverData}
        currentDriveObjectiveData={currentDriveObjectiveData}
        currentEcommerceTurnoverData={directDriveTurnoverData}
        pastEcommerceTurnoverData={pastEcommerceTurnoverData}
        currentDriveOrdersData={currentDriveOrdersData}
        currentLadOrdersData={currentLadOrdersData}
        lastYearDriveOrdersData={lastYearDriveOrdersData}
        lastYearLadOrdersData={lastYearLadOrdersData}
      />
      <DateWrapper>
        <MrclText style={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>
          Analyses récentes
        </MrclText>
      </DateWrapper>
      <RecentAnalysisRow>
        <NpsVerbatims />
        <LandingDetentionTopCards
          detentionTop200PgcData={detentionTop200PgcData}
          detention3000PgcData={detentionPgcData}
          detentionPgcError={!!detentionPgcError}
          detentionTop200PgcError={!!detentionTop200PgcError}
          detentionTypologyData={detentionTypologyData}
          detentionTypologyError={!!detentionTypologyError}
        />
      </RecentAnalysisRow>
    </Container>
  );
};

export default LandingPage;